import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useOnboardingStore } from '../store/onboardingStore'
import { useDraftStore, useOnboardingSubmission } from '../features/onboarding'
import type { AccountProduct } from '../features/product-segment'

export function useOnboardingGate(profile: AccountProduct, scenarioId = 'humanteq', scenarioVersion = 1, completionPath?: string) {
  const navigate = useNavigate()
  const userId = useAuthStore((s) => s.userId)
  const generation = useAuthStore((s) => s.sessionGeneration)
  const [readyFor, setReadyFor] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  const { submit, error: submissionError } = useOnboardingSubmission()
  const retry = useCallback(() => { setError(''); setRevision((n) => n + 1) }, [])
  useEffect(() => {
    let active = true
    if (!userId) return
    const current = () => active && useAuthStore.getState().userId === userId && useAuthStore.getState().sessionGeneration === generation
    const run = async () => {
      if (!current()) return
      const draft = useDraftStore.getState().draft
      const completed = profile.onboarded || (useOnboardingStore.getState().onboarded && useOnboardingStore.getState().onboardedForUserId === userId)
      if (completed) {
        if (draft?.ownerUserId === userId) useDraftStore.getState().clear(draft.id)
        useOnboardingStore.getState().setOnboarded(true, userId)
        setReadyFor(userId)
        return
      }
      const compatible = draft?.ownerUserId === userId && draft.segment === profile.segment && draft.scenarioId === scenarioId && draft.version === scenarioVersion
      if (compatible && draft.readyToSubmit) {
        if (await submit(draft) && current()) {
          if (completionPath) navigate(completionPath, { replace: true })
          setReadyFor(userId)
        }
      } else {
        if (!compatible && draft?.ownerUserId === userId) useDraftStore.getState().clear(draft.id)
        navigate('/onboarding', { replace: true })
      }
    }
    run().catch(() => { if (current()) setError('Не удалось загрузить профиль. Попробуйте ещё раз.') })
    return () => { active = false }
  }, [navigate, userId, generation, profile, revision, submit, scenarioId, scenarioVersion, completionPath])
  return { ready: readyFor === userId && userId !== null, error: error || submissionError, retry }
}
